<template>
  <div class="space-y-5">
    <p class="form-helper">
      Your footer’s look comes from the template — here you control what it lists. Service and
      area columns stay in sync with your site automatically; custom columns and links are yours.
    </p>

    <!-- Columns -->
    <section class="space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-overline">Columns</span>
        <button type="button" class="btn btn-ghost btn-xs" @click="addColumn">
          <EditorIcon name="plus" class="w-3.5 h-3.5" /> Column
        </button>
      </div>

      <div
        v-for="(col, ci) in model.columns"
        :key="ci"
        class="card card-body space-y-3">
        <div class="flex items-center gap-2">
          <input
            v-model="col.title"
            type="text"
            class="form-input flex-1"
            placeholder="Column heading">
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs"
            title="Move up"
            :disabled="ci === 0"
            @click="moveColumn(ci, -1)">
            <EditorIcon name="arrow-up" class="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs"
            title="Move down"
            :disabled="ci === model.columns.length - 1"
            @click="moveColumn(ci, 1)">
            <EditorIcon name="arrow-down" class="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs text-error"
            title="Remove column"
            @click="model.columns.splice(ci, 1)">
            <EditorIcon name="trash" class="w-3.5 h-3.5" />
          </button>
        </div>

        <label class="block">
          <span class="form-label">Content</span>
          <select v-model="col.source" class="form-select w-full mt-1">
            <option value="manual">{{ sourceLabel('manual') }}</option>
            <option value="services">{{ sourceLabel('services') }}</option>
            <option value="service_areas">{{ sourceLabel('service_areas') }}</option>
          </select>
        </label>

        <!-- Auto-synced column controls -->
        <div v-if="col.source !== 'manual'" class="space-y-2">
          <p class="alert alert-info text-xs">
            Auto-synced — lists your {{ col.source === 'services' ? 'services' : 'service areas' }}
            ({{ sourceCount(col.source) }} live). Add or edit them from your pages.
          </p>
          <div class="flex items-center justify-between gap-2">
            <span class="form-label">Max items</span>
            <input
              v-model.number="col.max_items"
              type="number"
              min="1"
              max="20"
              class="form-input w-20">
          </div>
          <label class="flex items-center justify-between gap-2">
            <span class="text-sm text-gray-800">Show a “View all” link</span>
            <input v-model="col.show_all" type="checkbox" class="form-checkbox">
          </label>
        </div>

        <!-- Manual link list -->
        <div v-else class="space-y-2">
          <LinkRows :links="col.links" />
          <button type="button" class="btn btn-ghost btn-xs" @click="addLink(col.links)">
            <EditorIcon name="plus" class="w-3.5 h-3.5" /> Link
          </button>
        </div>
      </div>
    </section>

    <!-- Social -->
    <section class="space-y-2">
      <span class="text-overline">Social links</span>
      <div
        v-for="(s, si) in model.social"
        :key="si"
        class="flex items-center gap-2">
        <select v-model="s.platform" class="form-select w-32">
          <option v-for="p in footer.social_platforms" :key="p" :value="p">
            {{ platformLabel(p) }}
          </option>
        </select>
        <input
          v-model="s.url"
          type="url"
          class="form-input flex-1"
          placeholder="https://instagram.com/yourstudio">
        <button
          type="button"
          class="btn btn-ghost btn-icon btn-xs text-error"
          title="Remove"
          @click="model.social.splice(si, 1)">
          <EditorIcon name="trash" class="w-3.5 h-3.5" />
        </button>
      </div>
      <button type="button" class="btn btn-ghost btn-xs" @click="addSocial">
        <EditorIcon name="plus" class="w-3.5 h-3.5" /> Social link
      </button>
    </section>

    <!-- Legal / bottom links -->
    <section class="space-y-2">
      <span class="text-overline">Legal links</span>
      <LinkRows :links="model.bottom_links" />
      <button type="button" class="btn btn-ghost btn-xs" @click="addLink(model.bottom_links)">
        <EditorIcon name="plus" class="w-3.5 h-3.5" /> Link
      </button>
    </section>

    <!-- Copyright -->
    <label class="block">
      <span class="form-label">Copyright line</span>
      <input
        v-model="model.copyright"
        type="text"
        class="form-input w-full mt-1"
        placeholder="© {year} {business}. All rights reserved.">
      <span class="form-helper">
        Use <code>{business}</code> for your business name and <code>{year}</code> for the current year.
      </span>
    </label>

    <button
      type="button"
      :disabled="saving"
      class="btn btn-primary btn-sm btn-full"
      @click="save">
      {{ saving ? 'Saving…' : 'Save footer' }}
    </button>
  </div>
</template>

<script setup>
import { computed, h, reactive, watch } from 'vue'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  footer: { type: Object, required: true },
  saving: { type: Boolean, default: false }
})
const emit = defineEmits(['save'])

// Small renderless-ish helper: a list of {text,url} rows with remove + reorder.
// Declared inline so the footer editor stays a single file.
const LinkRows = {
  props: { links: { type: Array, required: true } },
  setup(p) {
    const move = (i, d) => {
      const j = i + d
      if (j < 0 || j >= p.links.length) return
      const [row] = p.links.splice(i, 1)
      p.links.splice(j, 0, row)
    }
    return () => p.links.map((link, i) => h('div', { key: i, class: 'flex items-center gap-2' }, [
      h('input', {
        'value': link.text,
        'type': 'text',
        'class': 'form-input flex-1',
        'placeholder': 'Label',
        'onInput': (e) => { link.text = e.target.value }
      }),
      h('input', {
        'value': link.url,
        'type': 'text',
        'class': 'form-input flex-1',
        'placeholder': '/page or https://…',
        'onInput': (e) => { link.url = e.target.value }
      }),
      h('button', {
        'type': 'button',
        'class': 'btn btn-ghost btn-icon btn-xs',
        'title': 'Move up',
        'disabled': i === 0,
        'onClick': () => move(i, -1)
      }, h(EditorIcon, { name: 'arrow-up', class: 'w-3.5 h-3.5' })),
      h('button', {
        'type': 'button',
        'class': 'btn btn-ghost btn-icon btn-xs',
        'title': 'Move down',
        'disabled': i === p.links.length - 1,
        'onClick': () => move(i, 1)
      }, h(EditorIcon, { name: 'arrow-down', class: 'w-3.5 h-3.5' })),
      h('button', {
        'type': 'button',
        'class': 'btn btn-ghost btn-icon btn-xs text-error',
        'title': 'Remove',
        'onClick': () => p.links.splice(i, 1)
      }, h(EditorIcon, { name: 'trash', class: 'w-3.5 h-3.5' }))
    ]))
  }
}

function blankModel(content) {
  return {
    columns: (content.columns || []).map((c) => ({
      id: c.id || '',
      title: c.title || '',
      source: c.source || 'manual',
      max_items: c.max_items || 6,
      show_all: c.show_all !== false,
      links: (c.links || []).map((l) => ({ text: l.text || '', url: l.url || '' }))
    })),
    bottom_links: (content.bottom_links || []).map((l) => ({ text: l.text || '', url: l.url || '' })),
    social: (content.social || []).map((s) => ({ platform: s.platform || 'instagram', url: s.url || '' })),
    copyright: content.copyright || ''
  }
}

const model = reactive(blankModel(props.footer.content || {}))

// Re-sync when the parent hands back normalized content after a save.
watch(() => props.footer.content, (content) => {
  Object.assign(model, blankModel(content || {}))
})

const sources = computed(() => props.footer.sources || {})

function sourceLabel(key) {
  if (key === 'manual') return 'Custom links'
  return sources.value[key]?.label || key
}
function sourceCount(key) { return sources.value[key]?.count ?? 0 }
function platformLabel(p) { return p === 'x' ? 'X (Twitter)' : p.charAt(0).toUpperCase() + p.slice(1) }

function slugify(text) {
  return (text || 'links').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'links'
}

function addColumn() {
  model.columns.push({ id: '', title: 'Links', source: 'manual', max_items: 6, show_all: true, links: []})
}
function moveColumn(i, d) {
  const j = i + d
  if (j < 0 || j >= model.columns.length) return
  const [col] = model.columns.splice(i, 1)
  model.columns.splice(j, 0, col)
}
function addLink(arr) { arr.push({ text: '', url: '' }) }
function addSocial() { model.social.push({ platform: props.footer.social_platforms[0] || 'instagram', url: '' }) }

function save() {
  const payload = {
    columns: model.columns.map((c) => ({
      id: c.id || slugify(c.title),
      title: c.title,
      source: c.source,
      max_items: c.max_items,
      show_all: c.show_all,
      links: c.source === 'manual' ? c.links : []
    })),
    bottom_links: model.bottom_links,
    social: model.social,
    copyright: model.copyright
  }
  emit('save', payload)
}
</script>
